import { useEffect, useRef, useState } from 'react'
import { loadUserData, saveUserData } from './authStorage'
import type { Dispatch, SetStateAction } from 'react'

function resolveInitialValue<T>(defaultValue: T, persistKey: string | null, userEmail: string | null): T {
  if (!persistKey || !userEmail) {
    return defaultValue
  }

  const stored = loadUserData<T>(userEmail, persistKey)
  return stored ?? defaultValue
}

/**
 * Mode-aware state hook.
 *
 * - persistKey === null  → plain useState with defaultValue (mock mode)
 * - persistKey !== null  → hydrate from localStorage and sync via effect (real mode)
 */
export function usePersistedState<T>(
  defaultValue: T,
  persistKey: string | null,
  userEmail: string | null
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => resolveInitialValue(defaultValue, persistKey, userEmail))
  const defaultValueRef = useRef(defaultValue)
  const storageIdentityRef = useRef<string | null>(null)
  defaultValueRef.current = defaultValue

  useEffect(() => {
    const storageIdentity = persistKey && userEmail ? `${userEmail}::${persistKey}` : null
    if (storageIdentityRef.current === storageIdentity) {
      return
    }

    storageIdentityRef.current = storageIdentity
    const nextValue = resolveInitialValue(defaultValueRef.current, persistKey, userEmail)
    setValue((previousValue) => {
      if (Object.is(previousValue, nextValue)) {
        return previousValue
      }

      return nextValue
    })
  }, [persistKey, userEmail])

  useEffect(() => {
    if (!persistKey || !userEmail) {
      return
    }

    saveUserData(userEmail, persistKey, value)
  }, [persistKey, userEmail, value])

  return [value, setValue]
}
